// Walking-route PDF generator — A4 portrait, gegroepeerd per locatie
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export interface WalkingRouteItem {
  product_naam: string;
  product_id: string;
  bc: string;
  eenheid: string;
  locatie: string;
  quantity: number;
  ordernumbers: string[];
  isColor: boolean;
}

export interface WalkingRouteData {
  items: WalkingRouteItem[];
  orderCount: number;
  generatedAt: string;
  pickerName?: string;
}

// Natural sort op locatie (A.12.3 ordening)
const naturalLocationCompare = (a: string, b: string): number => {
  const re = /(\d+)|(\D+)/g;
  const ap = (a || "").match(re) || [];
  const bp = (b || "").match(re) || [];
  const len = Math.min(ap.length, bp.length);
  for (let i = 0; i < len; i++) {
    const an = parseInt(ap[i], 10);
    const bn = parseInt(bp[i], 10);
    if (!isNaN(an) && !isNaN(bn)) {
      if (an !== bn) return an - bn;
    } else {
      const c = ap[i].localeCompare(bp[i], "nl");
      if (c !== 0) return c;
    }
  }
  return ap.length - bp.length;
};

export function generateWalkingRoutePdf(data: WalkingRouteData): void {
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 12;

  // Sorteer items op locatie (smart route)
  const sorted = [...data.items].sort((a, b) => naturalLocationCompare(a.locatie, b.locatie));

  // Header
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("Slimme looproute — Luijten VVZ", margin, 16);

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  const subParts = [
    `Datum: ${data.generatedAt}`,
    `Orders: ${data.orderCount}`,
    `Regels: ${sorted.length}`,
  ];
  if (data.pickerName) subParts.push(`Picker: ${data.pickerName}`);
  doc.text(subParts.join("   |   "), margin, 22);

  // Tabel
  autoTable(doc, {
    startY: 28,
    margin: { left: margin, right: margin, bottom: 14 },
    head: [["Locatie", "Product", "BC", "Aantal", "Eenheid", "Tag", "Orders"]],
    body: sorted.map((it) => [
      it.locatie || "-",
      it.product_naam || it.product_id,
      it.bc || "-",
      String(it.quantity),
      it.eenheid || "",
      it.isColor ? "KLEUR" : "",
      it.ordernumbers.join(", "),
    ]),
    styles: {
      fontSize: 8,
      cellPadding: 1.8,
      overflow: "linebreak",
      valign: "middle",
    },
    headStyles: {
      fillColor: [30, 41, 59],
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    columnStyles: {
      0: { cellWidth: 22, fontStyle: "bold" },
      1: { cellWidth: 60 },
      2: { cellWidth: 18 },
      3: { cellWidth: 14, halign: "right", fontStyle: "bold" },
      4: { cellWidth: 14 },
      5: { cellWidth: 14, halign: "center" },
      6: { cellWidth: "auto" },
    },
    didParseCell: (hookData) => {
      if (hookData.section === "body" && hookData.column.index === 5) {
        const raw = hookData.cell.raw;
        if (raw === "KLEUR") {
          hookData.cell.styles.fillColor = [251, 191, 36];
          hookData.cell.styles.textColor = [120, 53, 15];
          hookData.cell.styles.fontStyle = "bold";
        }
      }
    },
    didDrawPage: () => {
      const pn = doc.getCurrentPageInfo().pageNumber;
      doc.setFontSize(8);
      doc.setTextColor(120);
      doc.text(`Pagina ${pn}`, pageWidth - margin, pageHeight - 6, { align: "right" });
      doc.setTextColor(0);
    },
  });

  const stamp = new Date()
    .toISOString()
    .slice(0, 16)
    .replace(/[-:T]/g, "");
  doc.save(`looproute-luijten-vvz-${stamp}.pdf`);
}
